// "Redeem voucher" section (§6.1 / §6.4 / A6).
//
// Staff burn a customer's redemption voucher. The customer presents either their
// short voucher CODE (the wallet's "show to staff" QR / text — the normal
// in-person path) or a signed `burnToken` from the checkout reserve flow. Both
// go to POST /guest/voucher/burn; the backend shape-detects which it got. The
// code path may bounce 409 `reward_choice_required` + options when the reward
// is a set/category — we render the chooser and re-submit with the pick.
//
// Input is camera-scan (html5-qrcode via the shared QrScanView) or paste/type.
//
// A6: only staff (validatetokenrestaurantuser) can burn; the staff identity is the
// Bearer token (req.decoded.userid), never the body (A4).
import React, { useState } from 'react';
import axios from 'axios';
import { API, authHeaders, describeError } from './api';
import QrScanView from '../../components/QrScanView';

export default function RedeemVoucher({ onAuthExpired }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [choices, setChoices] = useState(null); // reward_choice_required options
  const [result, setResult] = useState(null); // { rewardName, orderRef, replay }

  const submit = async (rewardProductId) => {
    if (busy) return;
    setError('');
    setResult(null);
    const burnToken = code.trim();
    if (!burnToken) {
      setError('Scan or paste the customer’s voucher code.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/guest/voucher/burn`,
        rewardProductId ? { burnToken, rewardProductId } : { burnToken },
        { headers: authHeaders() },
      );
      const data = res.data || {};
      const rewardName =
        data.rewardName ||
        (data.reward && (data.reward.name || data.reward.Name)) ||
        (data.rewardLine && data.rewardLine.name) ||
        null;
      if (data.ok === false) {
        setError(describeError({ response: { status: data.status || 409, data } }));
        return;
      }
      setResult({
        rewardName,
        orderRef: data.orderRef || null,
        replay: !!data.replay,
      });
      setCode('');
      setChoices(null);
    } catch (err) {
      if (err && err.response && (err.response.status === 401 || err.response.status === 403)) {
        if (onAuthExpired) {
          onAuthExpired();
          return;
        }
      }
      // §6.4 chooser handshake: the reward is a set/category — show the options
      // and re-submit with the customer's pick.
      const data = err && err.response && err.response.data;
      if (data && data.message === 'reward_choice_required' && Array.isArray(data.options)) {
        setChoices(data.options);
        return;
      }
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="staff-card">
      <h2>Redeem voucher</h2>
      <p className="staff-hint">
        Scan the customer’s voucher QR — or type the short code they show you —
        to give them their reward.
      </p>

      {error ? <div className="staff-alert is-error">{error}</div> : null}

      {result ? (
        <div className="staff-alert is-ok">
          <div className="staff-reward">
            <div className="staff-reward-icon">🎉</div>
            <div className="staff-reward-name">
              {result.rewardName || 'Reward redeemed'}
            </div>
            {result.orderRef ? (
              <div className="staff-reward-ref">
                Order <code>{result.orderRef}</code>
              </div>
            ) : null}
            {result.replay ? (
              <div className="staff-reward-ref">
                Already redeemed — showing the original order.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {scanning ? (
        <>
          <QrScanView
            onScan={(text) => {
              setScanning(false);
              setCode(String(text || '').trim());
            }}
            hint="Point the camera at the customer’s voucher QR"
          />
          <button
            className="staff-btn staff-btn-ghost"
            type="button"
            style={{ marginTop: 10 }}
            onClick={() => setScanning(false)}
          >
            Cancel scan
          </button>
        </>
      ) : (
        <>
          <label className="staff-label" htmlFor="burn-token">
            Voucher code
          </label>
          <textarea
            id="burn-token"
            className="staff-textarea"
            placeholder="Scan or type the voucher code…"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />

          {choices ? (
            <>
              <p className="staff-hint" style={{ marginTop: 10 }}>
                Which reward does the customer want?
              </p>
              {choices.map((o) => (
                <button
                  key={o.productId}
                  className="staff-btn staff-btn-ghost"
                  type="button"
                  disabled={busy || o.available === false}
                  style={{ marginTop: 8, opacity: o.available === false ? 0.5 : 1 }}
                  onClick={() => submit(o.productId)}
                >
                  {o.name}
                  {o.available === false ? ' (sold out)' : ''}
                </button>
              ))}
            </>
          ) : (
            <>
              <button className="staff-btn" type="button" disabled={busy} onClick={() => submit()}>
                {busy ? <span className="staff-spinner" /> : null}
                {busy ? 'Redeeming…' : 'Redeem reward'}
              </button>
              <button
                className="staff-btn staff-btn-ghost"
                type="button"
                style={{ marginTop: 10 }}
                onClick={() => { setError(''); setScanning(true); }}
              >
                📷 Scan QR
              </button>
            </>
          )}
        </>
      )}

      {result ? (
        <button
          className="staff-btn staff-btn-ghost"
          type="button"
          style={{ marginTop: 10 }}
          onClick={() => {
            setResult(null);
            setError('');
            setChoices(null);
          }}
        >
          Redeem another
        </button>
      ) : null}
    </div>
  );
}
